// Libraries
import { defineStore } from "pinia";

// Services & Helpers
import { formatQueryParams } from "@/utils/format";
import { apiService } from "@/core/services/apiService";
import { errorHandlerService } from "@/core/services/error";

// Composables
import { useUser } from "@/composables/useUser";
import { toast } from "@/composables/useCustomToast";

// Stores
import pinia from "@/store/pinia";
import { useTeamStore } from "@/store/team";
import { useProjectCreationStore } from "@/store/project-creation";

// Types
import { User } from "@/@types/team";
import { KnowledgeBaseT, Project } from "@/@types/project";
import { Persona } from "@/@types/persona";
import { ProjectFilter } from "@/@types/project";

export type ProjectStoreStateType = {
  projects: Project[];
  currentProject?: Project;
  totalProjectsCount: number;
  showDeleted: boolean;
  loading: boolean;
  loadingAudiences: boolean;
  listingPage: number;
  audiences: (Persona & { checked: boolean })[];
  audienceModal: boolean;
  deleteAudienceModal: boolean;
  selectedAudience: Persona | null;
  modalAction: "create" | "edit";
  knowledgeBases: KnowledgeBaseT[];
  knowledgeBasesCount: number;
  loadingKnowledgeBases: boolean;
  update: {
    name: string;
    coverUrl: string | null;
    description: string;
    differentials: string;
    language: string;
    mustHave: string;
    mustNotHave: string;
    brandVoice: {
      brandVoices: { label: string; checked: boolean }[];
      tones: { label: string; checked: boolean }[];
      languages: { label: string; checked: boolean }[];
      perspectives: { label: string; checked: boolean }[];
    };
    tags: string[];
    members: string[];
  };
};

const { userStatus, updateStatus } = useUser();

export const MAX_PROJECTS_PER_FETCH = 15;

type ProjectFetchResponse = {
  projects: Project[];
  totalProjectsCount: number;
};

export const useProjectStore = defineStore("Project", {
  state: (): ProjectStoreStateType => ({
    projects: [] as Project[],
    currentProject: undefined,
    totalProjectsCount: 0,
    showDeleted: false,
    loading: false,
    loadingAudiences: false,
    listingPage: 1,
    audiences: [],
    audienceModal: false,
    deleteAudienceModal: false,
    selectedAudience: null,
    modalAction: "create",
    knowledgeBases: [],
    knowledgeBasesCount: 0,
    loadingKnowledgeBases: false,
    update: {
      name: "",
      coverUrl: "",
      description: "",
      differentials: "",
      language: "",
      mustHave: "",
      mustNotHave: "",
      brandVoice: {
        brandVoices: [],
        tones: [],
        languages: [],
        perspectives: []
      },
      tags: [],
      members: []
    }
  }),
  actions: {
    // NOTE: Essa é a função mais generalista para realizar buscas de projetos
    // ela recebe como parâmetro um objeto com os filtros que serão aplicados
    async fetch(filter?: ProjectFilter) {
      let query: string = "";
      let filterToSend: ProjectFilter = filter || {};

      const teamId = userStatus.value?.activeTeam?.id;

      filterToSend.teamId = filter?.teamId || teamId;
      filterToSend.amount = filter?.amount || MAX_PROJECTS_PER_FETCH.toString();

      if (!filterToSend.teamId) return;

      query = formatQueryParams(filterToSend);

      const url = getProjectFetchUrlByUserRole(`projects?${query && "&" + query}`);

      try {
        const { data, error } = await apiService.get<ProjectFetchResponse>({ endpoint: url });

        if (!data || error) return;

        if (!filter?.shouldIgnoreSave) {
          this.projects = data.projects || data || [];
          this.totalProjectsCount = data.totalProjectsCount;

          await this.fetchCurrentProject();
        }

        return data.projects;
      } catch (error) {
        errorHandlerService.handleError(error);
        return;
      }
    },
    async fetchCurrentProject(projectId?: string, loading = true, setCurrentProject = true) {
      const creationStore = useProjectCreationStore(pinia);

      if (loading) {
        creationStore.projectLoading = true;
      }

      const userId = userStatus.value?.id;
      const id = projectId || userStatus.value?.lastActiveProject?.id || this.currentProject?.id;

      if (!userId || !id) {
        creationStore.projectLoading = false;

        errorHandlerService.handleError("error", {
          default: {
            title: "Erro ao buscar projeto",
            message: "Não foi possível buscar o projeto. Atualize a página e tente novamente."
          }
        });

        throw new Error("Não foi possível buscar o projeto");
      }

      const { data, error } = await apiService.get<Project>({ endpoint: `projects/${id}` });

      if (!data || error) {
        creationStore.projectLoading = false;

        return { error };
      }

      if (setCurrentProject) {
        this.currentProject = data;
      }

      if (loading) {
        creationStore.projectLoading = false;
      }

      return { data };
    },
    async fetchProjects(deleted = false, dates: [string, string] | null = null, name?: string) {
      const teamId = userStatus.value?.activeTeam?.id;

      let url = getProjectFetchUrlByUserRole(
        `projects?teamId=${teamId}&simplified=true&deleted=${deleted}&amount=${MAX_PROJECTS_PER_FETCH}${name ? `&name=${name}` : ""}`
      );

      if (dates) {
        url += `&startDate=${dates[0]}&endDate=${dates[1]}`;
      }

      if (!teamId) return;

      try {
        const { data, error } = await apiService.get<ProjectFetchResponse>({ endpoint: url });

        if (!data || error) return;

        this.projects = data.projects || data || [];
        this.totalProjectsCount = data.totalProjectsCount;

        await this.fetchCurrentProject();
        await this.includeProject();
      } catch (error) {
        errorHandlerService.handleError(error);
      }
    },
    async switchProject(id: string, successMessage = true) {
      this.loading = true;

      const userId = userStatus.value?.id;

      if (!userId) {
        errorHandlerService.handleError("auth/user-not-found", {
          default: {
            message: "Não foi possível trocar de projeto",
            title: "Erro ao trocar de projeto. Atualize a página e tente novamente."
          }
        });

        return;
      }

      try {
        const { data, error } = await apiService.patch({ endpoint: `users/switch-project/${id}` });

        if (error) {
          throw new Error(error);
        }

        const response = await this.fetchCurrentProject(id);

        if (response.error) {
          throw new Error(response.error);
        }

        const projectName =
          (this.currentProject?.name || "").length > 25
            ? this.currentProject?.name.slice(0, 25) + "..."
            : this.currentProject?.name;

        successMessage &&
          toast.success({
            title: "Uhuul!",
            message: `Agora você está no projeto ${projectName}!`,
            duration: 3000
          });

        userStatus.value.lastActiveProject.id = id;

        await Promise.all([this.includeProject(), updateStatus()]);

        return { data };
      } catch (error) {
        errorHandlerService.handleError(error);

        throw new Error("Não foi possível trocar de projeto");
      } finally {
        this.loading = false;
      }
    },
    async includeProject() {
      // NOTE: Essa função responsável por adicionar o projeto atual na lista de projetos caso ele já não esteja lá
      const userId = userStatus.value?.id;
      const currentId = this.currentProject?.id || userStatus.value?.lastActiveProject.id;

      if (!this.projects || !userId) return;

      if (!this.projects?.find((p: Project) => p.id === currentId) && currentId) {
        const { error, data } = await apiService.get<Project>({ endpoint: `projects/${currentId}` });

        if (!data || error) return;

        this.projects.push(data);
      }
    },
    async archiveProject(id: string, _deleted = false) {
      const { error } = await apiService.patch({ endpoint: `projects/${id}/archive` });
      return error;
    },
    async loadMoreProjects(filter: Partial<Record<keyof ProjectFilter, string | number | boolean>>) {
      // NOTE: a funrna um booleana que representa se há mais intens para carregar ou não, basicamente
      // se o resultado da busca tiver length 0 isso quer dizer que não há mais itens para carregar
      const query = formatQueryParams(filter);
      const teamId = userStatus.value?.activeTeam?.id;
      const url = getProjectFetchUrlByUserRole(
        `projects?teamId=${teamId}&simplified=true&amount=${MAX_PROJECTS_PER_FETCH}&${query}`
      );

      if (!teamId) return;
      const { data, error } = await apiService.get<ProjectFetchResponse>({ endpoint: url });

      if (!data || error || !data.projects.length) {
        return false;
      }

      let result = data.projects || [];
      result = result.filter((p1: Project) => !this.projects.find((p2: Project) => p2.id === p1.id));
      this.projects = [...this.projects, ...result];
      return true;
    },
    async createFolder(projectId: string, folderName: string, parentFolderId: string | null = null) {
      const { error } = await apiService.post({
        endpoint: `projects/${projectId}/folders`,
        body: {
          name: folderName,
          parentFolderId
        }
      });
      if (!!error) {
        return error;
      }
      this.fetchProjects();
      return error;
    },
    async updateUsers(projectId: string, user: User, action: "add" | "remove" = "add") {
      const teamStore = useTeamStore();
      const url = `teams/${teamStore.currentTeam?.id}/users/${user.email}`;

      const body: Record<string, unknown> = {
        projectId,
        removeFromProject: action !== "add"
      };

      const { error } = await apiService.patch({
        endpoint: url,
        body
      });
      return error;
    },
    getProject(id: string): Project | undefined {
      return this.projects.find((project: Project) => project.id === id);
    },
    async duplicateProject(id: string) {
      const { error } = await apiService.post({ endpoint: `projects/${id}/duplicate` });
      return error;
    },
    async fetchAudiences(simplified = false, setAudience = true) {
      const creationStore = useProjectCreationStore(pinia);

      const currProject = this.current;

      if (currProject?.audiences.length) {
        const teamId = userStatus.value.activeTeam.id;
        this.loadingAudiences = true;

        const res = await creationStore.getProjectAudiences(teamId, currProject.audiences, simplified);

        if (res && setAudience) {
          this.audiences = res.map(audience => ({ ...audience, checked: false }));
          creationStore.fields.audience = res;
        }

        this.loadingAudiences = false;

        return res ?? [];
      } else {
        this.audiences = [];

        return [];
      }
    },
    async fetchKnowledgeBases() {
      const creationStore = useProjectCreationStore(pinia);

      const teamId = this.currentProject?.id;

      if (!teamId) return;

      this.loadingKnowledgeBases = true;

      const res = await creationStore.getKnowledgeBases(teamId, 1, 9999);

      if (res) {
        this.knowledgeBases = res.knowledgeBases?.map(knowledgeBases => ({ ...knowledgeBases, checked: false }));
        this.knowledgeBasesCount = res.count;
      }

      this.loadingKnowledgeBases = false;
    },
    async fetchByTeamId(teamId: string) {
      const url = getProjectFetchUrlByUserRole(`projects?teamId=${teamId}`);
      try {
        const { data, error } = await apiService.get<ProjectFetchResponse>({ endpoint: url });
        if (!data || error) {
          return;
        }
        return data;
      } catch (error) {
        errorHandlerService.handleError(error);
        return;
      }
    }
  },
  getters: {
    current(state: ProjectStoreStateType): Project | undefined {
      return state.currentProject;
    },
    projectsListing(state: ProjectStoreStateType): Project[] | undefined {
      return state.projects;
    }
  }
});

const getProjectFetchUrlByUserRole = (url: string) => {
  const userId = userStatus.value?.id;
  const userFilter = ["admin", "manager"].includes(userStatus.value?.activeTeam?.role) ? "" : `&users=${userId}`;

  return url + userFilter;
};
